import React, { useState, useEffect } from 'react';
import baseapi from '../../../services/baseapi';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { RefreshCw } from 'lucide-react';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import { useSearchParams } from 'react-router-dom';

interface HistoryTabProps {
  agentId: string;
  clientId: string;
}

const fmtBytes = (n: number) => {
  if (!n) return "0 B";
  const u = ["B", "KiB", "MiB", "GiB", "TiB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return n.toFixed(n >= 100 ? 0 : 1) + " " + u[i];
};

const fmtCpu = (m: number) => {
  if (m == null) return "—";
  return m >= 1000 ? (m / 1000).toFixed(2) + " cores" : Math.round(m) + "m";
};

const HistoryTab: React.FC<HistoryTabProps> = ({ agentId, clientId }) => {
  const [nodes, setNodes] = useState<any[]>([]);
  const [pods, setPods] = useState<any[]>([]);
  const [loadingEntities, setLoadingEntities] = useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const involvedObjectUid = searchParams.get('involved_object_uid');

  const [histType, setHistType] = useState<'cluster' | 'node' | 'pod'>('cluster');
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [rangeHours, setRangeHours] = useState<number>(6);
  const [buckets, setBuckets] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [loadingAlerts, setLoadingAlerts] = useState<boolean>(false);
  const [intervalInfo, setIntervalInfo] = useState<string>('');

  // const isFirstMount = useRef(true);

  // Fetch nodes/pods on mount or credential change to populate options
  useEffect(() => {
    const fetchEntities = async () => {
      setLoadingEntities(true);
      try {
        const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
        const params: any = { agent_id: agentId, client_id: clientId };
        if (clusterName) {
          params.cluster_name = clusterName;
        }
        const res = await baseapi.get('/k8s_dashboard/snapshot', { params });
        if (res.data?.status && res.data?.data) {
          setNodes(res.data.data.nodes || []);
          setPods(res.data.data.pods || []);
        }
      } catch (error) {
        console.error('Error fetching entities for history:', error);
      } finally {
        setLoadingEntities(false);
      }
    };
    fetchEntities();
  }, [agentId, clientId]);

  // Handle entity selection default on type change
  useEffect(() => {
    if (histType === 'cluster') {
      setSelectedEntity('');
    } else if (histType === 'node') {
      if (nodes.length > 0) {
        setSelectedEntity(nodes[0].name || nodes[0].node_name || '');
      } else {
        setSelectedEntity('');
      }
    } else if (histType === 'pod') {
      if (pods.length > 0) {
        const firstPod = pods[0];
        setSelectedEntity(`${firstPod.namespace}/${firstPod.name || firstPod.pod_name}`);
      } else {
        setSelectedEntity('');
      }
    }
  }, [histType, nodes, pods]);

  const loadHistory = async () => {
    if (!agentId) return;
    setLoadingHistory(true);
    setBuckets([]);
    try {
      let endpoint = '';
      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        hours: rangeHours
      };
      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (histType === 'cluster') {
        endpoint = '/k8s_dashboard/history/cluster';
      } else if (histType === 'node') {
        endpoint = '/k8s_dashboard/history/node';
        params.name = selectedEntity;
      } else {
        endpoint = '/k8s_dashboard/history/pod';
        const [ns, name] = selectedEntity.split('/');
        params.namespace = ns || '';
        params.name = name || '';
      }

      const queryObj: any = {};
      if (involvedObjectUid) {
        queryObj.involved_object_uid = involvedObjectUid;
      }
      
      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      // Skip API call if entity is required but not yet populated in state
      if (histType !== 'cluster' && !selectedEntity) {
        setLoadingHistory(false);
        return;
      }

      const res = await baseapi.get(endpoint, { params });
      if (res.data?.status) {
        setBuckets(res.data.buckets || res.data.data?.buckets || []);
        setIntervalInfo(res.data.interval || res.data.data?.interval || '');
      }
    } catch (error) {
      console.error('Error fetching history data:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadAlerts = async () => {
    if (!agentId) return;
    setLoadingAlerts(true);
    try {
      const queryObj: any = {};
      if (involvedObjectUid) {
        queryObj.involved_object_uid = involvedObjectUid;
      }

      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        hours: rangeHours,
        event_type: 'Warning',
        limit: 200
      };
      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      const res = await baseapi.get('/k8s_dashboard/events', { params });
      if (res.data?.status) {
        setAlerts(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching alert history:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  // Re-fetch history & alerts when key configurations change
  useEffect(() => {
    if (histType === 'cluster' || selectedEntity) {
      loadHistory();
      loadAlerts();
    }
  }, [agentId, clientId, histType, selectedEntity, rangeHours, involvedObjectUid]);

  const formatXAxis = (tickItem: any) => {
    if (!tickItem) return '';
    const d = new Date(tickItem);
    return rangeHours > 48
      ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
      : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const formatCpuYAxis = (tick: any) => {
    if (histType === 'pod') {
      return `${tick}m`;
    }
    return `${tick}%`;
  };

  const formatMemYAxis = (tick: any) => {
    if (histType === 'pod') {
      return fmtBytes(tick);
    }
    return `${tick}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dateStr = label ? new Date(label).toLocaleString() : '';
      return (
        <div className="bg-gray-900 border border-gray-700 p-2.5 rounded shadow-lg font-mono text-[11px]">
          <p className="text-gray-200 mb-1">{dateStr}</p>
          {payload.map((entry: any, index: number) => {
            let val = entry.value;
            if (entry.name.toLowerCase().includes('memory') || entry.dataKey.toLowerCase().includes('mem')) {
              if (histType === 'pod') {
                val = fmtBytes(entry.value);
              } else {
                val = `${entry.value.toFixed(1)}%`;
              }
            } else if (entry.name.toLowerCase().includes('cpu') || entry.dataKey.toLowerCase().includes('cpu')) {
              if (histType === 'pod') {
                val = fmtCpu(entry.value);
              } else {
                val = `${entry.value.toFixed(1)}%`;
              }
            }
            return (
              <div key={index} className="flex items-center gap-1.5 text-white">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
                <span>{entry.name}:</span>
                <span className="font-bold">{val}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const typeOptions = [
    { label: 'Cluster', value: 'cluster' },
    { label: 'Node', value: 'node' },
    { label: 'Pod', value: 'pod' },
  ];

  const entityOptions = histType === 'node'
    ? nodes.map((n) => ({
        label: n.name || n.node_name || '',
        value: n.name || n.node_name || '',
      }))
    : pods.map((p) => {
        const podKey = `${p.namespace}/${p.name || p.pod_name}`;
        return {
          label: `${p.namespace} / ${p.name || p.pod_name}`,
          value: podKey,
        };
      });

  return (
    <div className="space-y-4">
      {/* Historical trends card */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden flex flex-col">
        <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-200 p-3 border-b border-gray-700 m-0 font-mono">
          <span className="w-1.5 h-1.5 bg-orange-500 rounded-sm"></span>
          Historical trends — stored in OpenSearch (day-wise indices)
        </h3>

        {involvedObjectUid && (
          <div className="bg-blue-500/10 border-b border-blue-500/30 p-2.5 flex justify-between items-center px-4">
            <span className="text-blue-400 font-mono text-[11px] font-bold tracking-wide">
              Filtered by Pod: {alerts.length > 0 ? (alerts[0].involved_object_name || alerts[0].involved_object || involvedObjectUid) : involvedObjectUid}
            </span>
            <button
              onClick={() => {
                setSearchParams(prev => {
                  const nextParams = new URLSearchParams(prev);
                  nextParams.delete('involved_object_uid');
                  return nextParams;
                });
              }}
              className="text-[10px] font-mono bg-blue-500/20 text-blue-300 px-2.5 py-1 rounded hover:bg-blue-500/30 transition-colors border border-blue-500/30 font-bold tracking-wider uppercase"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="p-3 border-b border-gray-700 bg-gray-900/50 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-gray-200">Type:</span>
            <CustomDropdown
              data={typeOptions}
              width="w-32"
              placeholder="Select Type"
              selectedValue={histType}
              onSelect={(val) => setHistType(val as any)}
              buttonHeight={8}
            />
          </div>

          {histType !== 'cluster' && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-gray-200">Entity:</span>
              <CustomDropdown
                data={entityOptions}
                width="w-64"
                placeholder={loadingEntities ? 'Loading...' : 'Select Entity'}
                selectedValue={selectedEntity}
                onSelect={(val) => setSelectedEntity(val)}
                buttonHeight={8}
                disabled={loadingEntities}
              />
            </div>
          )}

          <div className="flex items-center gap-1 font-mono">
            {([
              { label: '1h', value: 1 },
              { label: '6h', value: 6 },
              { label: '24h', value: 24 },
              { label: '7d', value: 168 },
              { label: '30d', value: 720 },
            ]).map((btn) => (
              <button
                key={btn.value}
                onClick={() => setRangeHours(btn.value)}
                className={`px-3 py-1 font-mono text-xs border rounded transition-colors ${
                  rangeHours === btn.value
                    ? 'text-orange-500 border-orange-500 bg-orange-500/10 font-bold'
                    : 'text-gray-200 border-gray-700 bg-gray-800 hover:text-white hover:border-gray-600'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              loadHistory();
              loadAlerts();
            }}
            className="flex items-center gap-1.5 px-3 py-1 bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 rounded font-mono text-xs transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loadingHistory ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {intervalInfo && (
            <span className="text-[10px] text-gray-200 font-mono">
              interval: {intervalInfo}
            </span>
          )}
        </div>

        {/* Charts area */}
        <div className="p-4 bg-gray-800">
          {loadingHistory ? (
            <div className="text-center text-gray-200 italic font-mono py-20">
              Loading historical trends data...
            </div>
          ) : buckets.length === 0 ? (
            <div className="text-center text-gray-200 italic font-mono py-20">
              No data yet for this selection — data accumulates as agents push to OpenSearch.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* CPU Chart */}
              <div className="bg-gray-900 border border-gray-700 rounded p-4 flex flex-col">
                <h4 className="text-[11px] font-mono font-bold tracking-wider text-gray-200 mb-4 uppercase">
                  {histType === 'pod' ? 'POD CPU (millicores)' : `${histType.toUpperCase()} CPU %`}
                </h4>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={buckets} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#242a33" />
                      <XAxis
                        dataKey="t"
                        tickFormatter={formatXAxis}
                        stroke="#acadb0ff"
                        fontSize={10}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={formatCpuYAxis}
                        stroke="#acadb0ff"
                        fontSize={10}
                        tickLine={false}
                        domain={histType !== 'pod' ? [0, 100] : undefined}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '10px', fontFamily: 'monospace' }} />
                      {histType === 'pod' ? (
                        <>
                          <Line
                            type="monotone"
                            name="CPU Usage"
                            dataKey="cpu_m"
                            stroke="#ff7a18"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                          <Line
                            type="monotone"
                            name="CPU Limit"
                            dataKey="cpu_lim_m"
                            stroke="#4a525e"
                            strokeWidth={1.5}
                            strokeDasharray="5 5"
                            dot={false}
                          />
                        </>
                      ) : (
                        <Line
                          type="monotone"
                          name="CPU Usage"
                          dataKey="cpu_pct"
                          stroke="#ff7a18"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Memory Chart */}
              <div className="bg-gray-900 border border-gray-700 rounded p-4 flex flex-col">
                <h4 className="text-[11px] font-mono font-bold tracking-wider text-gray-200 mb-4 uppercase">
                  {histType === 'pod' ? 'POD MEMORY' : `${histType.toUpperCase()} MEMORY %`}
                </h4>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={buckets} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#242a33" />
                      <XAxis
                        dataKey="t"
                        tickFormatter={formatXAxis}
                        stroke="#acadb0ff"
                        fontSize={10}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={formatMemYAxis}
                        stroke="#acadb0ff"
                        fontSize={10}
                        tickLine={false}
                        domain={histType !== 'pod' ? [0, 100] : undefined}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '10px', fontFamily: 'monospace' }} />
                      {histType === 'pod' ? (
                        <>
                          <Line
                            type="monotone"
                            name="Memory Usage"
                            dataKey="mem_bytes"
                            stroke="#ff7a18"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                          <Line
                            type="monotone"
                            name="Memory Limit"
                            dataKey="mem_lim_bytes"
                            stroke="#4a525e"
                            strokeWidth={1.5}
                            strokeDasharray="5 5"
                            dot={false}
                          />
                        </>
                      ) : (
                        <Line
                          type="monotone"
                          name="Memory Usage"
                          dataKey="mem_pct"
                          stroke="#ff7a18"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert timeline card */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden flex flex-col">
        <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-200 p-3 border-b border-gray-700 m-0 font-mono">
          <span className="w-1.5 h-1.5 bg-orange-500 rounded-sm"></span>
          Alert timeline (selected range)
        </h3>
        <div className="max-h-[340px] overflow-y-auto font-mono text-xs">
          {loadingAlerts ? (
            <div className="text-gray-200 p-4">Loading alert timeline...</div>
          ) : alerts.length === 0 ? (
            <div className="p-6 text-center text-gray-200 italic">No warning events in range</div>
          ) : (
            alerts.map((a, idx) => (
              <div key={idx} className="flex gap-2.5 p-2 border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors items-start">
                <span className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_6px_#f5b73d] flex-shrink-0 mt-1.5" />
                <span className="text-gray-100 whitespace-nowrap">
                  {a.event_time || a.collected_at ? new Date(a.event_time || a.collected_at).toLocaleString() : '—'}
                </span>
                <span className="text-orange-500 font-bold whitespace-nowrap">
                  {a.involved_object || a.object}
                </span>
                {a.namespace && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-gray-700 text-gray-300 font-bold">
                    {a.namespace}
                  </span>
                )}
                <span className="text-gray-200 break-all flex-1">{a.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryTab;
